'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { profilesApi, Profile } from '@/app/api/profiles';
import { setActiveProfileId, getActiveProfileId } from '@/lib/active-profile';
import { useAuth } from '@/context/AuthContext';

interface ProfileContextType {
  profiles: Profile[];
  activeProfile: Profile | null;
  activeProfileId: string | null;
  loading: boolean;
  setActiveProfile: (profile: Profile) => void;
  refreshProfiles: () => Promise<void>;
  createProfile: (name: string, avatarEmoji?: string, settings?: Record<string, any>, handle?: string) => Promise<Profile>;
  updateProfile: (id: string, updates: { name?: string; avatarEmoji?: string; settings?: Record<string, any>; handle?: string }) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfileId, setActiveProfileIdState] = useState<string | null>(getActiveProfileId());
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  const refreshProfiles = useCallback(async () => {
    if (!user) return;
    try {
      const data = await profilesApi.list();
      setProfiles(data);

      // Restore or default the active profile
      const stored = getActiveProfileId();
      const validStored = stored ? data.find(p => p.id === stored) : null;

      if (validStored) {
        setActiveProfileIdState(validStored.id);
        setActiveProfileId(validStored.id);
      } else {
        // Fall back to the default profile
        const defaultProfile = data.find(p => p.isDefault) || data[0] || null;
        if (defaultProfile) {
          setActiveProfileIdState(defaultProfile.id);
          setActiveProfileId(defaultProfile.id);
        } else {
          setActiveProfileIdState(null);
          setActiveProfileId(null);
        }
      }
    } catch (err) {
      console.error('[ProfileContext] Failed to load profiles:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && !hasFetched.current) {
      hasFetched.current = true;
      refreshProfiles();
    } else if (!user) {
      setProfiles([]);
      setActiveProfileIdState(null);
      setLoading(false);
    }
  }, [user, refreshProfiles]);

  const setActiveProfile = useCallback((profile: Profile) => {
    setActiveProfileIdState(profile.id);
    setActiveProfileId(profile.id);
  }, []);

  const createProfile = useCallback(async (name: string, avatarEmoji = '🧑', settings = {}, handle?: string): Promise<Profile> => {
    // Standardize handle if missing
    const finalHandle = handle || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const newProfile = await profilesApi.create({ name, avatarEmoji, settings, handle: finalHandle });
    await refreshProfiles();
    return newProfile;
  }, [refreshProfiles]);

  const updateProfile = useCallback(async (id: string, updates: { name?: string; avatarEmoji?: string; settings?: Record<string, any>; handle?: string }) => {
    await profilesApi.update(id, updates);
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  }, []);

  const deleteProfile = useCallback(async (id: string) => {
    await profilesApi.delete(id);
    await refreshProfiles();
  }, [refreshProfiles]);

  const activeProfile = profiles.find(p => p.id === activeProfileId) || profiles[0] || null;

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        activeProfile,
        activeProfileId,
        loading,
        setActiveProfile,
        refreshProfiles,
        createProfile,
        updateProfile,
        deleteProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextType => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within a ProfileProvider');
  return ctx;
};
